import { useState } from 'react';

import {
  formatVoiceDate,
  voiceProviderIsProduction,
  voiceProviderLabel,
} from '../../context/VoiceContext';
import { useVoice } from '../../hooks/useVoice';
import { api } from '../../services/api';

const VoiceSettingsPanel = () => {
  const {
    status,
    profile,
    settings,
    isLoading,
    hasActiveTwin,
    isMock,
    error,
    refresh,
  } = useVoice();

  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  if (isLoading || status === 'idle') {
    return (
      <section className="voice-settings-section" aria-label="Voice instellingen laden">
        <h3>Voice Agent</h3>
        <div className="voice-hydration small">
          <span className="ai-spinner" />
          <p>Voice-status laden…</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="voice-settings-section" aria-label="Voice instellingen fout">
        <h3>Voice Agent</h3>
        <p className="notice error small" role="alert">
          {error.message}
        </p>
        {error.canRetry && (
          <button type="button" className="button primary small" onClick={() => refresh()}>
            Opnieuw proberen
          </button>
        )}
      </section>
    );
  }

  if (!settings) {
    return (
      <section className="voice-settings-section" aria-label="Voice instellingen niet beschikbaar">
        <h3>Voice Agent</h3>
        <p className="notice info small">Voice-instellingen zijn niet beschikbaar.</p>
      </section>
    );
  }

  const handleUpdate = async (update: Partial<typeof settings>) => {
    setSaving(true);
    setLocalError(null);
    setSuccess(null);
    try {
      await api.updateVoiceSettings(update);
      await refresh();
      setSuccess('Opgeslagen.');
    } catch {
      setLocalError('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!profile) return;
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    try {
      await api.voiceDeleteProfile(profile.id);
      setDeleteConfirm(false);
      setSuccess('Voice Twin verwijderd.');
      await refresh();
    } catch {
      setLocalError('Verwijderen mislukt.');
      setDeleteConfirm(false);
    }
  };

  return (
    <section className="voice-settings-section">
      <h3>Voice Agent</h3>

      <div className="voice-status-card">
        <div className="voice-status-info">
          <div className="voice-status-badges">
            <span className={`voice-status-badge ${hasActiveTwin ? 'active' : 'inactive'}`}>
              {hasActiveTwin ? '● Voice Twin Actief' : '● Geen Voice Twin'}
            </span>
            {profile && !voiceProviderIsProduction(profile.provider) && (
              <span className="voice-status-badge warning">Test / ontwikkel</span>
            )}
          </div>
          {profile && (
            <span className="muted small">
              {voiceProviderLabel(profile.provider)} · {profile.languagePrimary} · v{profile.version}
              {profile.consentAcceptedAt ? ` · toestemming ${formatVoiceDate(profile.consentAcceptedAt)}` : ''}
            </span>
          )}
        </div>
        {profile && (
          <div className="voice-status-actions">
            {deleteConfirm ? (
              <div className="voice-confirm-inline">
                <p>Permanent verwijderen?</p>
                <div className="button-row">
                  <button type="button" className="button danger small" onClick={() => void handleDelete()}>
                    Ja
                  </button>
                  <button type="button" className="button secondary small" onClick={() => setDeleteConfirm(false)}>
                    Nee
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="button danger small"
                onClick={() => setDeleteConfirm(true)}
              >
                Verwijderen (GDPR)
              </button>
            )}
          </div>
        )}
      </div>

      {!profile && (
        <p className="notice info">
          Ga naar de <strong>Voice</strong> tab om je Voice Twin aan te maken via de enrollment wizard.
        </p>
      )}

      {isMock && (
        <p className="notice warning small">
          De actieve Voice Twin draait op een Mock-provider. Dit is alleen geschikt voor testen en ontwikkeling.
        </p>
      )}

      <div className="voice-toggles">
        <label className="voice-toggle-row">
          <div>
            <strong>Voice Auto-Answer</strong>
            <p className="muted small">AI mag inkomende gesprekken beantwoorden met je Voice Twin.</p>
          </div>
          <input
            type="checkbox"
            checked={settings.voiceAutomode}
            disabled={saving || !hasActiveTwin}
            onChange={(e) => void handleUpdate({ voiceAutomode: e.target.checked })}
          />
        </label>

        <label className="voice-toggle-row danger">
          <div>
            <strong>Emergency Stop (Kill Switch)</strong>
            <p className="muted small">Stopt onmiddellijk alle AI-stembeantwoording.</p>
          </div>
          <input
            type="checkbox"
            checked={settings.killSwitch}
            disabled={saving}
            onChange={(e) => void handleUpdate({ killSwitch: e.target.checked })}
          />
        </label>

        <label className="voice-toggle-row">
          <div>
            <strong>Alleen tijdens kantooruren</strong>
            <p className="muted small">AI beantwoordt alleen gesprekken binnen bedrijfsuren.</p>
          </div>
          <input
            type="checkbox"
            checked={settings.businessHoursOnly}
            disabled={saving}
            onChange={(e) => void handleUpdate({ businessHoursOnly: e.target.checked })}
          />
        </label>
      </div>

      <label className="tool-field">
        <span>Maximale gespreksduur (minuten)</span>
        <input
          type="number"
          min={1}
          max={60}
          value={settings.maxCallDurationMin}
          disabled={saving}
          onChange={(e) => void handleUpdate({ maxCallDurationMin: Number(e.target.value) || 15 })}
        />
      </label>

      <label className="tool-field">
        <span>Bedrijfsnaam (voor AI begroeting)</span>
        <input
          type="text"
          value={settings.businessName}
          maxLength={200}
          disabled={saving}
          onChange={(e) => void handleUpdate({ businessName: e.target.value })}
          placeholder="Bijv. AI MessageBot"
        />
      </label>

      <label className="tool-field">
        <span>Toon van stem</span>
        <input
          type="text"
          value={settings.toneOfVoice}
          maxLength={500}
          disabled={saving}
          onChange={(e) => void handleUpdate({ toneOfVoice: e.target.value })}
          placeholder="Bijv. Vriendelijk, professioneel, beknopt"
        />
      </label>

      <label className="tool-field">
        <span>FAQ / prijzen samenvatting (voor AI context)</span>
        <textarea
          value={settings.faqSummary}
          maxLength={4000}
          disabled={saving}
          onChange={(e) => void handleUpdate({ faqSummary: e.target.value })}
          placeholder="Bijv. We zijn open ma-vr 9-17. Levertijd 2-3 dagen. Gratis bezorging vanaf €50."
          rows={4}
        />
      </label>

      <label className="tool-field">
        <span>Voorkeurstaal</span>
        <select
          value={settings.languagePreference}
          disabled={saving}
          onChange={(e) => void handleUpdate({ languagePreference: e.target.value })}
        >
          <option value="nl">Nederlands</option>
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </label>

      {localError && <p className="notice error small" role="alert">{localError}</p>}
      {success && <p className="notice success small" role="status">{success}</p>}
    </section>
  );
};

export default VoiceSettingsPanel;
